import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppShell from './shell/AppShell';
import useSidebarOrder from '../hooks/useSidebarOrder';
import { exportPipelinesToCSV } from '../utils/exportUtils';
import { API_BASE_URL } from '../config/api';
import MonthlyStatsWidget from './MonthlyStatsWidget';
import LeaderboardWidget from './LeaderboardWidget';

const API_BASE = API_BASE_URL;

/**
 * PipelinesPage — Shows all searches that have candidates on pipeline.
 * Allows recruiters to see which searches need active project management.
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortBy, setSortBy] = useState('card_count');
  const [firmFilter, setFirmFilter] = useState(null);
  const [savedFirmOrder, setSavedFirmOrder] = useSidebarOrder('pipelines');

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/api/pipelines/pipelines-overview`, { headers });
        setPipelines(res.data);
      } catch (err) {
        console.error('Error loading pipelines:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Unique client firms for sidebar
  const clientFirms = useMemo(() => {
    const firmMap = new Map();
    pipelines.forEach(p => {
      if (p.firm_name && p.firm_id) firmMap.set(String(p.firm_id), p.firm_name);
    });
    return Array.from(firmMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [pipelines]);

  // Sidebar items with drag-drop ordering
  const sidebarItems = useMemo(() => {
    const base = clientFirms.map(([id, name]) => ({
      key: id,
      label: name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2),
      title: name,
      active: firmFilter === id,
      onClick: () => setFirmFilter(firmFilter === id ? null : id),
    }));
    if (!savedFirmOrder) return base;
    // Reorder based on saved order, append any new ones at end
    const ordered = [];
    savedFirmOrder.forEach(key => {
      const item = base.find(i => i.key === key);
      if (item) ordered.push(item);
    });
    base.forEach(item => {
      if (!ordered.find(o => o.key === item.key)) ordered.push(item);
    });
    return ordered;
  }, [clientFirms, firmFilter, savedFirmOrder]);

  const handleSidebarReorder = useCallback((newItems) => {
    setSavedFirmOrder(newItems.map(i => i.key));
  }, [setSavedFirmOrder]);

  const filtered = useMemo(() => {
    let list = pipelines;

    if (statusFilter !== 'all') {
      list = list.filter(p => p.status === statusFilter);
    }

    if (firmFilter !== null) {
      list = list.filter(p => String(p.firm_id) === firmFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.firm_name || '').toLowerCase().includes(q) ||
        (p.location || '').toLowerCase().includes(q)
      );
    }

    // Sort
    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'card_count': return b.card_count - a.card_count;
        case 'name': return (a.name || '').localeCompare(b.name || '');
        case 'firm_name': return (a.firm_name || '').localeCompare(b.firm_name || '');
        case 'created_at': return new Date(b.created_at) - new Date(a.created_at);
        default: return 0;
      }
    });

    return list;
  }, [pipelines, statusFilter, firmFilter, searchQuery, sortBy]);

  const totalCandidates = useMemo(() =>
    filtered.reduce((sum, p) => sum + p.card_count, 0)
  , [filtered]);

  // KPIs — computed from the currently-loaded pipelines. "Active
  // Searches" counts pipelines where status is active AND category is
  // executive_search (or blank/default). Total pipelines counts
  // everything loaded, and total candidates sums card_count across all.
  const activeSearchesCount = useMemo(() =>
    pipelines.filter(p =>
      (p.status || 'active') === 'active' &&
      (!p.category || p.category === 'executive_search')
    ).length
  , [pipelines]);

  const totalCandidatesInPipelines = useMemo(() =>
    pipelines.reduce((sum, p) => sum + (p.card_count || 0), 0)
  , [pipelines]);

  const stageColor = (name) => {
    const n = (name || '').toLowerCase();
    if (n === 'sent' || n === 'submitted') return 'bg-blue-100 text-blue-700';
    if (n === 'interviewing' || n.includes('interview')) return 'bg-purple-100 text-purple-700';
    if (n === 'offered' || n === 'offer') return 'bg-orange-100 text-orange-700';
    if (n === 'placed' || n === 'hired') return 'bg-green-100 text-green-700';
    if (n === 'rejected' || n === 'declined' || n === 'disqualified') return 'bg-red-100 text-red-600';
    if (n === 'hold' || n === 'on hold') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-600';
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ── PAGE STRIP ────────────────────────────────────────────────────────
  // Horizontal client-firm filter pills replace the old DraggableMiniSidebar.
  // "All Companies" acts as the previous topButton; firm pills follow with
  // the 2-letter abbreviation. Full firm name in the title attribute for
  // hover context.
  const pageStrip = (
    <div className="h-10 flex items-center px-4 gap-1.5 overflow-x-auto">
      <button
        onClick={() => setFirmFilter(null)}
        className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
          firmFilter === null
            ? 'bg-emerald-100 text-emerald-800'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }`}
      >
        All Companies
      </button>
      {sidebarItems.map((item) => (
        <button
          key={item.key}
          onClick={item.onClick}
          className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
            item.active
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          title={item.title}
        >
          {item.label}
        </button>
      ))}
      <div className="flex-1" />
      <button
        onClick={() => exportPipelinesToCSV(filtered)}
        className="flex-shrink-0 text-xs px-2 py-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors flex items-center gap-1.5"
        title="Export current list to CSV"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Export
      </button>
    </div>
  );

  return (
    <AppShell pageStrip={pageStrip}>
      <div className="h-full flex flex-col overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Toolbar */}
          <div className="bg-white border-b px-4 py-1.5 flex-shrink-0">
            <div className="flex items-center gap-2">
              {/* Active firm label */}
              {firmFilter !== null && (
                <>
                  <span className="text-xs font-semibold text-gray-500 truncate max-w-[140px]">
                    {clientFirms.find(([id]) => id === firmFilter)?.[1] || 'Unknown'}
                  </span>
                  <button
                    onClick={() => setFirmFilter(null)}
                    className="text-gray-400 hover:text-gray-600"
                    title="Clear filter"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}

              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search pipelines..."
                  className="w-52 pl-7 pr-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <svg className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700"
              >
                <option value="active">Active</option>
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-700"
              >
                <option value="card_count">Most Candidates</option>
                <option value="name">Name A-Z</option>
                <option value="firm_name">Client A-Z</option>
                <option value="created_at">Newest First</option>
              </select>

              <div className="flex-1" />

              {/* Summary */}
              <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
                <span className="font-medium text-gray-500">{filtered.length}</span> pipelines
                <span className="mx-1 text-gray-300">/</span>
                <span className="font-medium text-gray-500">{totalCandidates}</span> candidates
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto">

            {/* KPI Row — top of page dashboard signals. For now just
                Active Searches; grows into a proper stats strip in a
                follow-up (see Monthly Stats concept). */}
            <div className="mx-4 mt-3 mb-2">
              <div className="bg-white rounded-lg border shadow-sm p-4 flex items-center gap-8">
                <div>
                  <div className="text-2xl font-bold text-gray-900">{activeSearchesCount}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold mt-0.5">Active Searches</div>
                </div>
                <div className="h-8 w-px bg-gray-200" />
                <div>
                  <div className="text-2xl font-bold text-gray-900">{pipelines.length}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold mt-0.5">Total Pipelines</div>
                </div>
                <div className="h-8 w-px bg-gray-200" />
                <div>
                  <div className="text-2xl font-bold text-gray-900">{totalCandidatesInPipelines}</div>
                  <div className="text-[10px] text-gray-500 uppercase tracking-wide font-semibold mt-0.5">Candidates in Pipeline</div>
                </div>
              </div>
            </div>

            {/* Analytics widgets row — Monthly Stats + Leaderboard.
                Monthly Stats takes 2/3 (it has 4 metrics), Leaderboard
                takes 1/3 (5 rows of names/counts). On smaller screens
                (below lg) they stack full-width. */}
            <div className="mx-4 mb-3 grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="lg:col-span-2">
                <MonthlyStatsWidget />
              </div>
              <div className="lg:col-span-1">
                <LeaderboardWidget />
              </div>
            </div>

            {/* Pipelines Table */}
            {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="text-gray-500 text-sm">No pipelines with candidates found</p>
              <p className="text-xs text-gray-400 mt-1">
                {statusFilter !== 'all' ? 'Try changing the status filter.' : 'Add candidates to a search pipeline to see them here.'}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm" style={{ minWidth: 'max-content' }}>
              <thead className="bg-gray-50 border-b sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: 280 }}>Search</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: 160 }}>Client</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: 140 }}>Location</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: 80 }}>Pipeline</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage Breakdown</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: 80 }}>Status</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: 100 }}>Created</th>
                  <th className="w-auto"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(p => (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/search/${p.id}?tab=pipeline&sub=kanban`)}
                    className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                  >
                    {/* Search name */}
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900 truncate" style={{ maxWidth: 260 }}>{p.name}</div>
                      <div className="text-[10px] text-gray-400">SR-{String(p.id).padStart(4, '0')}</div>
                    </td>

                    {/* Client */}
                    <td className="px-3 py-2.5 text-gray-600 truncate" style={{ maxWidth: 160 }}>
                      {p.firm_name || '—'}
                    </td>

                    {/* Location */}
                    <td className="px-3 py-2.5 text-gray-500 truncate" style={{ maxWidth: 140 }}>
                      {p.location || '—'}
                    </td>

                    {/* Pipeline count */}
                    <td className="px-3 py-2.5 text-center">
                      <span className="inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                        {p.card_count}
                      </span>
                    </td>

                    {/* Stage breakdown */}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {p.stages.length > 0 ? (
                          p.stages.map((s, i) => (
                            <span key={i} className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded ${stageColor(s.name)}`}>
                              {s.name} <span className="font-bold">{s.count}</span>
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-3 py-2.5">
                      <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${
                        p.status === 'active' ? 'bg-green-100 text-green-700' :
                        p.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        {p.status}
                      </span>
                    </td>

                    {/* Created */}
                    <td className="px-3 py-2.5 text-xs text-gray-400 whitespace-nowrap">
                      {formatDate(p.created_at)}
                    </td>

                    <td></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>{/* close main content */}
    </div>{/* close h-full wrapper */}
    </AppShell>
  );
};

export default Dashboard;
